import {
  Flex,
  Text,
  Divider,
  Icon,
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { TbChecks, TbAlertTriangle, TbCircleX, TbCircleMinus } from "react-icons/tb";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { longAttributeStyles } from "components/DataDisplay/styles";
import RecipientTag from "components/Tag/ResourceTargetTag/RecipientTag";
import { CHECK_TARGET_LABELS, DELIVERY_METHOD_LABELS } from "features/Resources/Shared/constants";
import { dateInFormat } from "helpers/time";
import CheckpointTaskList from "features/Resources/Checkpoint/Components/CheckpointTaskList";
import CheckpointSendRemindersButton from "features/Resources/Checkpoint/Components/CheckpointSendRemindersButton";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";

const MAX_WIDTH = "500px";

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: any; label: string }> = {
  complete: { color: "green.600", bg: "green.50", icon: TbChecks, label: "Good to Go!" },
  partial: { color: "blue.500", bg: "blue.50", icon: TbAlertTriangle, label: "Needs a Nudge" },
  behind: { color: "red.600", bg: "red.50", icon: TbCircleX, label: "Behind Schedule" },
  skipped: { color: "gray.500", bg: "gray.50", icon: TbCircleMinus, label: "Skipped — No Tasks" },
  pending: { color: "gray.600", bg: "gray.50", icon: null, label: "Pending Evaluation" },
};

interface CheckpointDrawerFormProps {
  checkpoint: any;
}

export default function CheckpointDrawerForm({ checkpoint }: CheckpointDrawerFormProps) {
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(checkpoint?.newHireJourneyId);

  if (!checkpoint) return null;

  const config = STATUS_CONFIG[checkpoint.status] || STATUS_CONFIG.pending;
  const isEvaluated = checkpoint.evaluatedAt != null;
  const completionPct =
    checkpoint.tasksTotal > 0 ? Math.round((checkpoint.tasksComplete / checkpoint.tasksTotal) * 100) : 100;

  return (
    <Flex direction="column" gap="1">
      <HorizontalGreyBlackField
        label="Check"
        labelContainerProps={longAttributeStyles.labelContainerProps}
        maxWidth={MAX_WIDTH}
      >
        {CHECK_TARGET_LABELS[checkpoint.checkTarget] || checkpoint.checkTarget}
      </HorizontalGreyBlackField>

      <HorizontalGreyBlackField
        label="Notify via"
        labelContainerProps={longAttributeStyles.labelContainerProps}
        maxWidth={MAX_WIDTH}
      >
        <Text fontSize="sm">
          {DELIVERY_METHOD_LABELS[checkpoint.deliveryMethod] || checkpoint.deliveryMethod}
          {checkpoint.recipients?.length > 1 &&
            !checkpoint.recipients.some((r: any) => r.targetType === "MessageServiceChannel") && (
              <Text as="span" fontSize="xs" color="content.tertiary" ml="1">
                (sent as one {checkpoint.deliveryMethod === "message" ? "message" : "email"})
              </Text>
            )}
        </Text>
      </HorizontalGreyBlackField>

      {checkpoint.recipients && checkpoint.recipients.length > 0 && (
        <HorizontalGreyBlackField
          label="Recipients"
          labelContainerProps={longAttributeStyles.labelContainerProps}
          maxWidth={MAX_WIDTH}
        >
          <Flex gap="1" wrap="wrap">
            {checkpoint.recipients.map((r: any) => (
              <RecipientTag key={r.targetId} resourceId={r.targetId} resourceType={r.targetType} disabled />
            ))}
          </Flex>
        </HorizontalGreyBlackField>
      )}

      <HorizontalGreyBlackField
        label="Scheduled"
        labelContainerProps={longAttributeStyles.labelContainerProps}
        maxWidth={MAX_WIDTH}
      >
        <Text fontSize="sm">
          {dateInFormat(checkpoint.evaluateAt, "MMM D, YYYY [at] h:mm A", checkpoint.timezone)}
          {checkpoint.evaluateAtDescriptor && (
            <Text as="span" fontSize="xs" color="content.tertiary" ml="1">
              ({checkpoint.evaluateAtDescriptor})
            </Text>
          )}
        </Text>
      </HorizontalGreyBlackField>

      {isEvaluated && (
        <>
          <Divider my="3" />

          <Box bg={config.bg} borderRadius="lg" p="4" border="1px solid" borderColor={config.color} opacity="0.9">
            <Flex align="center" justify="space-between">
              <Flex align="center" gap="3">
                {config.icon && <Icon as={config.icon} boxSize="5" color={config.color} />}
                <Box>
                  <Text fontWeight="700" fontSize="sm" color={config.color}>
                    {config.label}
                  </Text>
                  <Text fontSize="xs" color={config.color} opacity="0.8">
                    Evaluated {dateInFormat(checkpoint.evaluatedAt, "MMM D, YYYY [at] h:mm A", checkpoint.timezone)}
                  </Text>
                </Box>
              </Flex>
              <CheckpointSendRemindersButton
                status={checkpoint.status}
                newHireJourney={newHireJourney}
                results={checkpoint.results}
              />
            </Flex>
          </Box>

          <SimpleGrid columns={3} spacing="3" mt="3">
            <Stat bg="white" borderRadius="md" border="1px solid" borderColor="gray.100" p="3">
              <StatLabel fontSize="xs" color="content.tertiary">
                Completion
              </StatLabel>
              <StatNumber fontSize="xl" color={config.color}>
                {completionPct}%
              </StatNumber>
              <StatHelpText fontSize="xs" mb="0">
                {checkpoint.tasksComplete} of {checkpoint.tasksTotal}
              </StatHelpText>
            </Stat>

            <Stat bg="white" borderRadius="md" border="1px solid" borderColor="gray.100" p="3">
              <StatLabel fontSize="xs" color="content.tertiary">
                Complete
              </StatLabel>
              <StatNumber fontSize="xl" color="green.600">
                {checkpoint.tasksComplete}
              </StatNumber>
              <StatHelpText fontSize="xs" mb="0">
                of {checkpoint.tasksTotal} tasks
              </StatHelpText>
            </Stat>

            <Stat bg="white" borderRadius="md" border="1px solid" borderColor="gray.100" p="3">
              <StatLabel fontSize="xs" color="content.tertiary">
                Overdue
              </StatLabel>
              <StatNumber fontSize="xl" color={checkpoint.tasksOverdue > 0 ? "red.600" : "green.600"}>
                {checkpoint.tasksOverdue}
              </StatNumber>
              <StatHelpText fontSize="xs" mb="0">
                {checkpoint.tasksOverdue > 0 ? "need attention" : "on track"}
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          <CheckpointTaskList results={checkpoint.results} compact />
        </>
      )}

      {!isEvaluated && (
        <Box bg="gray.50" borderRadius="lg" p="4" mt="3" textAlign="center">
          <Text color="content.tertiary" fontSize="sm">
            This checkpoint has not been evaluated yet.
          </Text>
        </Box>
      )}
    </Flex>
  );
}
